import React from 'react';
import GridCountCell from './grid/GridCountCell';

// =============================================================================
// TrackingPanel
// =============================================================================
// The LC dashboard's "Tracking" panel: one row per tracking-date type (ETA,
// Rail/Port LFD, Doc Cutoff, Cutoff, Per Diem, Whse LFD), one column per how many
// days that date is from today — 4+, 3, 2, 1, 0, <0 (past due) — plus a row Total.
// Unlike the Delivery Schedule (colored by a cell's calendar date), the urgency here
// is encoded by the column itself: 1 day out = yellow, today = orange, past due = red.
//
// Purely presentational — it renders the counts the backend returns and reports cell
// clicks via onSelectCell. Lives inside a <DashboardTile> (which supplies the title +
// drag handle), so it renders bare. Pairs with /lc-dashboards/tracking-counts.

export interface TrackingRow {
  d4plus: number;
  d3: number;
  d2: number;
  d1: number;
  d0: number;
  dneg: number;
  total: number;
}

export interface TrackingData {
  rows: { [rowKey: string]: TrackingRow };  // counts keyed by row key (missing = all 0)
}

export interface TrackingPanelProps {
  data: TrackingData | null;
  loading: boolean;
  // Fired when a non-zero count is clicked. `row` + `bucket` identify the cell for the
  // drill-down; `title` is a ready-made label for the results header.
  onSelectCell?: (row: string, bucket: string, title: string) => void;
}

// Days-out columns. `field` reads the count off a TrackingRow; `bucket` is the
// backend cell key; `color` is the urgency fill (only applied when count > 0).
const COLS: { field: keyof TrackingRow; label: string; bucket: string; color: string }[] = [
  { field: 'd4plus', label: '4+', bucket: '4plus', color: '' },
  { field: 'd3', label: '3', bucket: '3', color: '' },
  { field: 'd2', label: '2', bucket: '2', color: '' },
  { field: 'd1', label: '1', bucket: '1', color: 'bg-yellow-300 text-gray-900' },
  { field: 'd0', label: '0', bucket: '0', color: 'bg-orange-500 text-white' },
  { field: 'dneg', label: '<0', bucket: 'neg', color: 'bg-red-600 text-white' },
];

// Date-type rows, in display order.
const ROWS: { key: string; label: string }[] = [
  { key: 'eta', label: 'ETA' },
  { key: 'rail_port_lfd', label: 'Rail/Port LFD' },
  { key: 'doc_cutoff', label: 'Doc Cutoff' },
  { key: 'cutoff', label: 'Cutoff' },
  { key: 'per_diem', label: 'Per Diem' },
  { key: 'whse_lfd', label: 'Whse LFD' },
];

const EMPTY_ROW: TrackingRow = { d4plus: 0, d3: 0, d2: 0, d1: 0, d0: 0, dneg: 0, total: 0 };

const TrackingPanel: React.FC<TrackingPanelProps> = ({ data, loading, onSelectCell }) => {
  const placeholder = (value: number) => value === 0 && loading && !data;

  return (
    // w-full so the table fills the tile width (header + cells stretch to the panel
    // edges); natural height keeps the tile's double-click fit-to-content working.
    <div className="w-full bg-panel text-sm">
      <table className="border-collapse w-full">
        <thead>
          <tr className="bg-slate-700 text-white">
            <th className="text-left font-semibold px-2 py-1 min-w-[120px]">Tracking</th>
            {COLS.map((c) => (
              <th key={c.bucket} className="text-right font-semibold px-2 py-1 w-[56px]">{c.label}</th>
            ))}
            <th className="text-right font-semibold px-2 py-1 w-[64px]">Total</th>
          </tr>
        </thead>
        <tbody className="text-body">
          {ROWS.map((row) => {
            const counts = data?.rows[row.key] ?? EMPTY_ROW;
            return (
              <tr key={row.key} className="border-t border-gray-100 dark:border-gray-700/50">
                <td className="px-2 py-1 whitespace-nowrap text-label">{row.label}</td>
                {COLS.map((c) => {
                  const value = counts[c.field];
                  return (
                    <td key={c.bucket} className="border-l border-gray-100 dark:border-gray-700/50">
                      <GridCountCell
                        value={value}
                        colorClass={value > 0 ? c.color : ''}
                        placeholder={placeholder(value)}
                        onSelect={onSelectCell ? () => onSelectCell(row.key, c.bucket, `${row.label} – ${c.label}`) : undefined}
                        title={`View orders for ${row.label} (${c.label})`}
                      />
                    </td>
                  );
                })}
                <td className="border-l border-gray-300 dark:border-gray-600">
                  <GridCountCell
                    value={counts.total}
                    bold
                    placeholder={placeholder(counts.total)}
                    onSelect={onSelectCell ? () => onSelectCell(row.key, 'total', `${row.label} – Total`) : undefined}
                    title={`View all orders for ${row.label}`}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default TrackingPanel;
